import {
  Button,
  Icon,
  Select,
  SelectOptionType,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';

import {
  getContinuityPaymentSettings,
  getPeriodAndSchool,
  saveContinuityPaymentSettings,
} from '../../../api/requests/tuitionContinuityTariff';
import DisplayError from '../../../components/info/DisplayError';
import Loading from '../../../components/info/Loading';
import Tooltip from '../../../components/tooltip/Tooltip';
import { useLoadingState } from '../../../hooks/useLoadingState';
import { ContinuityFirstInstallmentRule } from '../../../types/tuitionContinuityTrariff';
import useFirstInstallmentRule from '../../academicOffers/AdmissionProcesses/hooks/useFirstInstallmentRule';
import {
  formatFirstInstallmentRule,
  getFirstInstallmentRule,
} from '../../academicOffers/AdmissionProcesses/utils';

// Prefijo de los campos dinámicos de la regla de primera cuota (mismo patrón
// que AdmissionProcessDetailsForm: evita colisión de nombres con
// maxInstallments/payday del propio formulario).
const RULE_PREFIX = 'rule';

const EMPTY_RULE_FIELDS = {
  [`${RULE_PREFIX}_selectDate`]: '',
  [`${RULE_PREFIX}_selectMonth`]: null,
  [`${RULE_PREFIX}_selectYear`]: null,
  [`${RULE_PREFIX}_selectDay`]: '',
  [`${RULE_PREFIX}_validityRange`]: '',
};

// Catálogo local del enum `Payday` del backend (5, 4, -5, -4, 15 — los
// negativos son válidos). No se reutiliza el form-data de Configuraciones >
// Financiero: ese endpoint está bajo SettingsPermission y esta pantalla corre
// bajo el permiso de continuidad, así que un admin sin acceso a Portal
// Financiero recibiría un 403. Tampoco se agrega un endpoint de opciones a
// esta pantalla: el catálogo es fijo (no configurable por tenant), así que se
// deriva localmente, con las mismas 5 combinaciones que
// `payday/enums/paydays.enum.ts::PaydayCodesText` en la API.
const PAYDAY_OPTION_CATALOG: { value: number; labelKey: string }[] = [
  { value: 5, labelKey: 'firstFiveDays' },
  { value: 4, labelKey: 'firstFourDays' },
  { value: -5, labelKey: 'lastFiveDays' },
  { value: -4, labelKey: 'lastFourDays' },
  { value: 15, labelKey: 'fortnight' },
];

export default function TcPaymentSettings() {
  const { t } = useTranslation();
  const prefix = 'tuitionContinuityTariff.paymentSettings';

  const {
    loading: periodsLoading,
    setLoading: setPeriodsLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState();

  const [periods, setPeriods] = useState<{ id: number; name: string }[]>([]);
  const [periodId, setPeriodId] = useState<number | undefined>();
  const [settingsLoading, setSettingsLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  // 404 del GET: el período no tiene calendario de continuidad configurado.
  // No todos los períodos que entrega getPeriodAndSchool tienen uno.
  const [noCalendar, setNoCalendar] = useState<boolean>(false);
  const [currentRule, setCurrentRule] = useState<
    ContinuityFirstInstallmentRule | null
  >(null);

  const methods = useForm<any>({
    defaultValues: {
      maxInstallments: null,
      payday: null,
      [`${RULE_PREFIX}_firstInstallmentRule`]: null,
      ...EMPTY_RULE_FIELDS,
    },
  });

  const paydayOptions = useMemo<SelectOptionType[]>(
    () =>
      PAYDAY_OPTION_CATALOG.map((p) => ({
        value: p.value,
        label: t(`${prefix}.paydayOptions.${p.labelKey}`),
      })),
    [t, prefix],
  );

  const maxInstallmentsOptions = useMemo<SelectOptionType[]>(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        label: (i + 1).toString(),
        value: i + 1,
      })),
    [],
  );

  const firstInstallmentRuleValue = methods.watch(
    `${RULE_PREFIX}_firstInstallmentRule`,
  );
  const { options: ruleOptions, formFields } = useFirstInstallmentRule({
    key: firstInstallmentRuleValue?.value as string,
    control: methods.control,
    fieldPrefix: RULE_PREFIX,
  });

  const texts = useMemo(
    () => ({
      title: t(`${prefix}.title`),
      subTitle: t(`${prefix}.subTitle`),
      selectPeriod: t(`${prefix}.form.selectPeriod`),
      selectPeriodHint: t(`${prefix}.selectPeriodHint`),
      noCalendar: t(`${prefix}.noCalendar`),
      firstInstallmentRule: t(`${prefix}.form.firstInstallmentRule`),
      maxInstallments: t(`${prefix}.form.maxInstallments`),
      maxInstallmentsTooltip: t(`${prefix}.form.maxInstallmentsTooltip`),
      payday: t(`${prefix}.form.payday`),
      paydayTooltip: t(`${prefix}.form.paydayTooltip`),
      save: t(`${prefix}.save`),
      error: { data: t(`${prefix}.error.data`) },
    }),
    [t, prefix],
  );

  const loadPeriods = useCallback(async () => {
    setPeriodsLoading(true);
    const { data, error } = await getPeriodAndSchool();
    if (error) {
      setErrorLoading(error.code);
    }
    if (data) {
      setErrorLoading(undefined);
      setPeriods(data.data.periods);
    }
    setPeriodsLoading(false);
  }, [setPeriodsLoading, setErrorLoading]);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  const handleChangePeriod = useCallback((option: SelectOptionType) => {
    setPeriodId(Number(option.value));
  }, []);

  // Carga (o recarga tras guardar) la configuración de pago del período
  // seleccionado y prellena el formulario. Siempre parte de un estado en
  // blanco para no arrastrar valores del período anterior.
  const loadSettings = useCallback(
    async (id: number) => {
      setSettingsLoading(true);
      setNoCalendar(false);
      const { data, error } = await getContinuityPaymentSettings(id);
      if (error) {
        if (error.status === 404) {
          setNoCalendar(true);
          setCurrentRule(null);
          methods.reset({
            maxInstallments: null,
            payday: null,
            [`${RULE_PREFIX}_firstInstallmentRule`]: null,
            ...EMPTY_RULE_FIELDS,
          });
        } else {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t(`${prefix}.toasts.loadError`),
          });
        }
        setSettingsLoading(false);
        return;
      }
      if (!data) {
        setSettingsLoading(false);
        return;
      }

      setCurrentRule(data.firstInstallmentRule);
      methods.reset({
        maxInstallments:
          data.maxInstallments !== null && data.maxInstallments !== undefined
            ? {
                value: data.maxInstallments,
                label: String(data.maxInstallments),
              }
            : null,
        payday:
          data.payday !== null && data.payday !== undefined
            ? paydayOptions.find((p) => p.value === data.payday) ?? null
            : null,
        [`${RULE_PREFIX}_firstInstallmentRule`]: null,
        ...EMPTY_RULE_FIELDS,
      });

      if (data.firstInstallmentRule) {
        const {
          key,
          selectDate,
          selectMonth,
          selectYear,
          selectDay,
          validityRange,
        } = getFirstInstallmentRule(data.firstInstallmentRule);

        const selectedOption = ruleOptions.find(
          (option) => option.value === key,
        );
        methods.setValue(`${RULE_PREFIX}_firstInstallmentRule`, {
          label: selectedOption?.label ?? '',
          value: selectedOption?.value ?? '',
        });
        if (selectDate) {
          methods.setValue(`${RULE_PREFIX}_selectDate`, selectDate);
        }
        if (selectMonth) {
          methods.setValue(`${RULE_PREFIX}_selectMonth`, selectMonth);
        }
        if (selectYear) {
          methods.setValue(`${RULE_PREFIX}_selectYear`, selectYear);
        }
        if (selectDay) {
          methods.setValue(`${RULE_PREFIX}_selectDay`, selectDay);
        }
        if (validityRange) {
          methods.setValue(
            `${RULE_PREFIX}_validityRange`,
            validityRange.toString(),
          );
        }
      }

      setSettingsLoading(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods, paydayOptions, ruleOptions, t, prefix],
  );

  useEffect(() => {
    if (periodId === undefined) {
      return;
    }
    loadSettings(periodId);
    // Solo debe recargar cuando cambia el período: `loadSettings` cambia de
    // referencia en cada render (depende de `methods`/`ruleOptions`) y
    // volver a incluirla en las deps dispararía un loop de refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId]);

  const onSubmit = useCallback(
    async (values: any) => {
      if (periodId === undefined) {
        return;
      }
      setSaving(true);

      const formattedData = {
        ...values,
        firstInstallmentRule: values[`${RULE_PREFIX}_firstInstallmentRule`],
        selectDate: values[`${RULE_PREFIX}_selectDate`],
        selectMonth: values[`${RULE_PREFIX}_selectMonth`],
        selectYear: values[`${RULE_PREFIX}_selectYear`],
        selectDay: values[`${RULE_PREFIX}_selectDay`],
        validityRange: values[`${RULE_PREFIX}_validityRange`],
      };

      // `formatFirstInstallmentRule` retorna null si no hay regla seleccionada
      // en el form; en ese caso se manda `undefined` (campo omitido) para que
      // el backend NO toque la regla ya vinculada. El selector de regla es
      // no-clearable (ver abajo) precisamente para que esta rama nunca
      // represente "el admin quiso desvincular la regla": el backend no
      // soporta desvincular una regla ya seteada (firstInstallmentRuleValue:
      // null es un no-op ahí), así que esta pantalla no ofrece esa opción.
      const firstInstallmentRuleValue = formatFirstInstallmentRule(
        formattedData,
        currentRule?.id,
      );

      const { error } = await saveContinuityPaymentSettings({
        periodId,
        maxInstallments: values.maxInstallments?.value ?? null,
        payday: values.payday?.value ?? null,
        firstInstallmentRuleValue: firstInstallmentRuleValue ?? undefined,
      });

      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${prefix}.toasts.saveError`),
        });
        setSaving(false);
        return;
      }

      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${prefix}.toasts.saveSuccess`),
      });
      await loadSettings(periodId);
      setSaving(false);
    },
    [periodId, currentRule, loadSettings, t, prefix],
  );

  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={texts.error.data}
        retryAction={() => loadPeriods()}
        loadingAction={periodsLoading}
      />
    );
  }
  if (periodsLoading) {
    return <Loading insideCard />;
  }

  const disabled = saving || settingsLoading || noCalendar;

  return (
    <Container fluid className="g-table-container pt-4">
      <p className="fs-20 text-uppercase text-primary fw-700">
        {texts.title}
      </p>
      <p>{texts.subTitle}</p>
      <Row>
        <Col md={6}>
          <Select
            name="selection-period"
            label={texts.selectPeriod}
            isClearable={false}
            options={periods.map((p) => ({ label: p.name, value: p.id }))}
            onChange={handleChangePeriod}
          />
        </Col>
      </Row>

      {periodId === undefined && (
        <p className="fs-14 text-medium mt-3">{texts.selectPeriodHint}</p>
      )}

      {periodId !== undefined && settingsLoading && (
        <Loading insideCard className="mt-4" />
      )}

      {periodId !== undefined && !settingsLoading && noCalendar && (
        <p className="fs-14 text-danger mt-3">{texts.noCalendar}</p>
      )}

      {periodId !== undefined && !settingsLoading && !noCalendar && (
        <>
          <hr className="my-4" />
          <Row>
            <Col md={4}>
              <Select
                name={`${RULE_PREFIX}_firstInstallmentRule`}
                label={texts.firstInstallmentRule}
                options={ruleOptions}
                control={methods.control}
                disabled={disabled}
                // No-clearable a propósito: el backend no puede desvincular
                // una regla ya guardada (ver comentario en onSubmit). Un
                // selector "limpiable" acá implicaría una acción que la API
                // no honra.
                isClearable={false}
              />
            </Col>
            <Col md={4}>
              <Row xs={12}>
                <Col xs={11}>
                  <Select
                    name="maxInstallments"
                    label={texts.maxInstallments}
                    options={maxInstallmentsOptions}
                    control={methods.control}
                    disabled={disabled}
                    // Vaciarlo vuelve a `null`, es decir "usar el máximo
                    // general" (ver onSubmit: `?.value ?? null`).
                    isClearable
                  />
                </Col>
                <Col
                  xs={1}
                  className="d-flex align-items-center"
                  style={{ marginLeft: '-14px' }}
                >
                  <Tooltip
                    id="info_tooltip_continuity_max_installments"
                    placement="bottom"
                    text={texts.maxInstallmentsTooltip}
                  >
                    <Icon name="information_input" className="mb-3" />
                  </Tooltip>
                </Col>
              </Row>
            </Col>
            <Col md={4}>
              <Row xs={12}>
                <Col xs={11}>
                  <Select
                    name="payday"
                    label={texts.payday}
                    options={paydayOptions}
                    control={methods.control}
                    disabled={disabled}
                    isClearable
                  />
                </Col>
                <Col
                  xs={1}
                  className="d-flex align-items-center"
                  style={{ marginLeft: '-14px' }}
                >
                  <Tooltip
                    id="info_tooltip_continuity_payday"
                    placement="bottom"
                    text={texts.paydayTooltip}
                  >
                    <Icon name="information_input" className="mb-3" />
                  </Tooltip>
                </Col>
              </Row>
            </Col>
          </Row>
          {formFields}

          <div className="d-flex justify-content-end mt-3">
            <Button
              size="md"
              className="px-5 font-weight-bold"
              text={texts.save}
              onClick={methods.handleSubmit(onSubmit)}
              loading={saving}
              disabled={disabled}
            />
          </div>
        </>
      )}
    </Container>
  );
}
